import { useMemo } from "react";

import { FileNumberShortPattern, useAlertContext, useFormikWithDirtyForm } from "@shared";
import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Yup from "yup";

import { SrvProceedingResponse, srvProceedingApi } from "@/api";
import { withLocalErrorHandling } from "@/api/middleware/errorMiddleware";
import { useCourts } from "@/common/hooks/useCourts";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { SrvProceedingFormValues } from "../SrvProceedingTab.types";
import {
  EMPTY_SRV_PROCEEDING,
  getSrvProceedingErrorMessage,
  mapFormValuesToRequest,
  mapResponseToFormValues,
} from "../SrvProceedingTab.utils";

const TRANSLATION_BASE = "clients.client.debtSettlement.srvProceeding";

interface Params {
  clientId: string;
  /** The opened Verfahren; undefined while a new one is drafted. */
  proceedingId: string | undefined;
  onCreated: (proceedingId: string) => void;
}

export function useSrvProceedingForm({ clientId, proceedingId, onCreated }: Params) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const queryClient = useQueryClient();
  const { courts } = useCourts();

  const { data, isError } = useQuery<SrvProceedingResponse>({
    queryKey: [QueryKey.SrvProceeding, clientId, proceedingId],
    queryFn: proceedingId
      ? () => srvProceedingApi.getSrvProceedingById({ clientId, proceedingId }, withLocalErrorHandling)
      : skipToken,
    meta: { loadingScope: "pageContent" },
  });

  const saveMutation = useMutation({
    mutationFn: (values: SrvProceedingFormValues) => {
      const saveSrvProceedingRequest = mapFormValuesToRequest(values);
      return proceedingId
        ? srvProceedingApi.updateSrvProceeding(
            { clientId, proceedingId, saveSrvProceedingRequest },
            withLocalErrorHandling
          )
        : srvProceedingApi.createSrvProceeding({ clientId, saveSrvProceedingRequest }, withLocalErrorHandling);
    },
    onSuccess: async (saved) => {
      queryClient.setQueryData([QueryKey.SrvProceeding, clientId, saved.id], saved);
      // The list shows the Aktenzahl and the opening date as the title. Awaited: a created Verfahren is
      // selected right after this, and a list that does not contain it yet would fall back to an older one.
      await queryClient.invalidateQueries({ queryKey: [QueryKey.SrvProceeding, clientId], exact: true });
      // Aktenzahl and Gericht are Detailsuche filters, and the result list is cached with
      // staleTime: Infinity — without this a saved Verfahren never reaches an open search.
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Clients] });

      showSuccess(t(proceedingId ? `${TRANSLATION_BASE}.saveSuccess` : `${TRANSLATION_BASE}.createSuccess`));
    },
    onError: (error) =>
      showError(
        getSrvProceedingErrorMessage(
          error,
          t,
          t(proceedingId ? `${TRANSLATION_BASE}.saveFailed` : `${TRANSLATION_BASE}.createFailed`)
        )
      ),
  });

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        applicationDate: Yup.date().nullable().typeError(t("common.validation.invalidDateFormat")),
        openingDate: Yup.date().nullable().typeError(t("common.validation.invalidDateFormat")),
        annulmentDate: Yup.date().nullable().typeError(t("common.validation.invalidDateFormat")),
        fileNumber: Yup.string().matches(FileNumberShortPattern, {
          message: t(`${TRANSLATION_BASE}.validation.invalidFileNumber`),
          excludeEmptyString: true,
        }),
        courtId: Yup.string()
          .nullable()
          .when("fileNumber", {
            is: (fileNumber: string | undefined) => !!fileNumber,
            then: (schema) =>
              schema
                .required(t("clients.client.debtSettlement.srvProceeding.validation.courtRequiredForFileNumber"))
                .test(
                  "court-file-number-prefix",
                  t("clients.client.debtSettlement.srvProceeding.validation.courtWithoutFileNumberPrefix"),
                  (courtId) => !!courts.find((court) => court.id === courtId)?.fileNumberPrefix
                ),
          }),
        courseEntries: Yup.array().of(
          Yup.object().shape({
            date: Yup.date()
              .typeError(t("common.validation.invalidDateFormat"))
              .required(t("common.validation.fieldRequired", { fieldName: t(`${TRANSLATION_BASE}.course.date`) })),
          })
        ),
      }),
    [t, courts]
  );

  const initialValues: SrvProceedingFormValues = useMemo(
    () => (data ? mapResponseToFormValues(data) : EMPTY_SRV_PROCEEDING),
    [data]
  );

  const formik = useFormikWithDirtyForm<SrvProceedingFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values, helpers) => {
      const saved = await saveMutation.mutateAsync(values).catch(() => null);
      if (saved && !proceedingId) {
        // Clean the draft first, so moving to the new Verfahren is not taken for leaving unsaved changes.
        helpers.resetForm({ values: mapResponseToFormValues(saved) });
        onCreated(saved.id);
      }
    },
  });

  // A stored participant that was deactivated after being assigned stays blocking only while the
  // form still references it — picking another active one or clearing the selection resolves it
  // (same rule as the claim form's inactive representative).
  const isInactiveTrusteeSelected =
    !!data?.trusteeId && data.isTrusteeInactive === true && formik.values.trusteeId === data.trusteeId;
  const isInactiveInsolvencyAdministratorSelected =
    !!data?.insolvencyAdministratorId &&
    data.isInsolvencyAdministratorInactive === true &&
    formik.values.insolvencyAdministratorId === data.insolvencyAdministratorId;

  return {
    formik,
    proceeding: data,
    isError,
    isInactiveTrusteeSelected,
    isInactiveInsolvencyAdministratorSelected,
  };
}
